import * as React from 'react';
import { useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  Table,
  Header,
  Icon,
  Button,
  PropertyFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { Link } from 'src/components/common/Link';
import { createSmusSsoGroupDetailLink } from 'src/routes';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import {
  mediumPageSizePreference,
  defaultWrapLinesPreference,
  i18nStrings,
  paginationLabels,
} from 'src/commons/tables';
import { getSmusProjectUrl, getSmusCreateProjectUrl, ProjectEntry } from 'src/components/utils/smusUtil';
import { compareBy } from 'src/components/utils/sorting';

export interface ProjectsTableProps {
  projects: ProjectEntry[];
  loading: boolean;
  title: string;
}

export const ProjectsTable = (props: ProjectsTableProps) => {
  const columnDefinitions: TableProps.ColumnDefinition<ProjectEntry>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (e) => (
        <Link external={true} href={getSmusProjectUrl(e.projectId)}>
          {e.name}
        </Link>
      ),
      sortingField: 'name',
    },
    { id: 'projectId', header: 'Project ID', cell: (e) => e.projectId, sortingField: 'projectId' },
    { id: 'accountId', header: 'Host account', cell: (e) => e.accountId, sortingField: 'accountId' },
    {
      id: 'groupName',
      header: 'Group',
      cell: (e) => <Link href={createSmusSsoGroupDetailLink(e.groupMetadataId)}>{e.groupName}</Link>,
      sortingField: 'groupName',
    },
    { id: 'createdBy', header: 'Created by', cell: (e) => e.createdBy, sortingField: 'createdBy' },
    { id: 'createdAt', header: 'Created at', cell: (e) => e.createdAt, sortingField: 'createdAt' },
  ];

  const contentDisplayOption: CollectionPreferencesProps.ContentDisplayOption[] = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header as string,
  }));

  const contentDisplayItem: CollectionPreferencesProps.ContentDisplayItem[] = [
    { id: 'name', visible: true },
    { id: 'projectId', visible: true },
    { id: 'accountId', visible: true },
    { id: 'groupName', visible: true },
    { id: 'createdBy', visible: true },
    { id: 'createdAt', visible: true },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
    contentDisplay: contentDisplayItem,
  });

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    props.projects,
    {
      filtering: {},
      pagination: { pageSize: preferences.pageSize },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'name',
            sortingComparator: compareBy('name', 'accountId', 'groupName'),
          },
        },
      },
      propertyFiltering: {
        empty: <EmptyState title='No projects' subtitle='No projects found.' />,
        filteringProperties: [
          {
            propertyLabel: 'Project name',
            key: 'name',
            groupValuesLabel: 'Project names',
          },
          {
            propertyLabel: 'Group',
            key: 'groupName',
            groupValuesLabel: 'Groups',
          },
          {
            propertyLabel: 'Account',
            key: 'accountId',
            groupValuesLabel: 'Accounts',
          },
        ],
      },
    },
  );

  return (
    <Table
      {...collectionProps}
      loading={props.loading}
      loadingText='Loading projects'
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={preferences.wrapLines}
      resizableColumns={true}
      columnDisplay={preferences.contentDisplay}
      empty={<EmptyState title='No projects' subtitle='No projects found.' />}
      header={
        <Header
          variant='h2'
          counter={`(${props.projects.length})`}
          actions={
            <Button target='_blank' variant='primary' disabled={props.loading} href={getSmusCreateProjectUrl()}>
              Create project in SMUS <Icon name='external' />
            </Button>
          }
        >
          {props.title}
        </Header>
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title='Preferences'
          confirmLabel='Confirm'
          cancelLabel='Cancel'
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={mediumPageSizePreference}
          wrapLinesPreference={defaultWrapLinesPreference}
          contentDisplayPreference={{
            title: 'Project',
            options: contentDisplayOption,
          }}
        />
      }
      filter={
        <PropertyFilter
          {...propertyFilterProps}
          disabled={props.loading}
          i18nStrings={i18nStrings}
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
    />
  );
};
